import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchProducts, fetchRates } from "../lib/api";

/**
 * Tre tal under heroen, sat som en aflæsning på en måler.
 *
 * Alle tre er sande og kommer fra data, ikke fra marketing: antallet af aftaler
 * er længden af den liste resultatsiden selv regner på, og datoen er den
 * satsdato API'et svarer med. PRODUCT.md forbyder eksplicit at påstå et antal
 * aftaler uden at tallet kommer fra data, så strimlen vises slet ikke før vi
 * har dem, og aldrig med et gæt eller et nul.
 *
 * Kaldene er gratis her: main.tsx varmer de samme to URL'er op ved opstart, og
 * `cachedGet` i api.ts deler løftet, så det her er et cache-hit.
 */

interface Aflæsning {
  antal: number;
  dato: string;
}

function formatDato(iso: string, lang: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(lang.startsWith("en") ? "en-GB" : "da-DK", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function HeroReadout() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [data, setData] = useState<Aflæsning | null>(null);

  useEffect(() => {
    let alive = true;
    // Den gamle aflæsning bliver stående mens en ny hentes, så et sprogskift
    // ikke får strimlen til at blinke.
    Promise.all([fetchProducts(lang), fetchRates(lang)])
      .then(([produkter, rates]) => {
        if (alive) setData({ antal: produkter.length, dato: rates.gaelderDato });
      })
      .catch(() => {
        /* Ingen tal, ingen strimmel. Pladsen står tom i stedet for at lyve. */
      });
    return () => {
      alive = false;
    };
  }, [lang]);

  return (
    <div className="mt-7 min-h-[57px]">
      {data && (
        <ul className="readout-in flex flex-wrap items-baseline gap-x-9 gap-y-2.5 border-y border-line py-3.5">
          <Tal v={String(data.antal)} label={t("hero.fig1")} />
          <Tal v={t("hero.fig2n")} label={t("hero.fig2")} />
          <Tal v={formatDato(data.dato, lang)} label={t("hero.fig3")} />
        </ul>
      )}
    </div>
  );
}

const Tal = ({ v, label }: { v: string; label: string }) => (
  <li className="flex items-baseline gap-2">
    <span className="num text-[17px] font-semibold leading-none text-ink">{v}</span>
    <span className="font-mono text-[10.5px] uppercase tracking-[0.13em] text-ink-faint">{label}</span>
  </li>
);
